import { Helmet } from "react-helmet-async";
import { BlogArticle } from "@/types/blog";

// Organization schema for GoDyspo
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "GoDyspo",
  url: "https://godyspo.com",
  logo: "https://godyspo.com/favicon.ico",
  description: "The streamlined real estate marketplace connecting sellers with investors.",
  foundingDate: "2024",
  sameAs: [
    "https://twitter.com/godyspo"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    url: "https://godyspo.com/contact"
  }
};

// WebSite schema with search action
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "GoDyspo",
  url: "https://godyspo.com",
  description: "Real estate marketplace for wholesalers, agents, and investors.",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://godyspo.com/buyer/search?q={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  }
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface StructuredDataProps {
  type: "Organization" | "WebSite" | "BreadcrumbList" | "FAQPage" | "SoftwareApplication" | "Article" | "MobileApplication";
  breadcrumbs?: BreadcrumbItem[];
  faqs?: { question: string; answer: string }[];
  article?: BlogArticle;
}

// MobileApplication schema for Google Play Store app
const mobileApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  "name": "GoDyspo: Real Estate Market",
  "operatingSystem": "ANDROID",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "Real Estate Software",
  "description": "The #1 real estate disposition platform for wholesalers and investors. Find off-market wholesale deals, manage buyers, and close faster.",
  "downloadUrl": "https://play.google.com/store/apps/details?id=app.lovable.a458feef936a4fdd9306ef7eae220648",
  "installUrl": "https://play.google.com/store/apps/details?id=app.lovable.a458feef936a4fdd9306ef7eae220648",
  "screenshot": "https://godyspo.com/og-image.png",
  "softwareVersion": "1.0",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free to download. Free for buyers. Sellers start with a 30-day free trial"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "ratingCount": "1"
  },
  "author": {
    "@type": "Organization",
    "name": "GoDyspo"
  }
};

// SoftwareApplication schema for rich snippets
const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "GoDyspo",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Real Estate Software",
  operatingSystem: "Web Browser",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free for buyers. Sellers start with a 30-day free trial"
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "150"
  },
  description: "Real estate disposition platform for wholesalers and agents to list properties and manage buyers."
};

// Generate Article schema
const generateArticleSchema = (article: BlogArticle) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.excerpt || article.meta_description || "",
  image: article.cover_image_url || "https://godyspo.com/og-image.png",
  author: {
    "@type": "Person",
    name: article.author_name
  },
  publisher: {
    "@type": "Organization",
    name: "GoDyspo",
    logo: {
      "@type": "ImageObject",
      url: "https://godyspo.com/favicon.ico"
    }
  },
  datePublished: article.published_at,
  dateModified: article.updated_at,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://godyspo.com/blog/${article.slug}`
  },
  articleSection: article.category,
  keywords: article.tags?.join(", ") || ""
});

/**
 * Component for adding JSON-LD structured data to pages
 */
const StructuredData = ({ type, breadcrumbs, faqs, article }: StructuredDataProps) => {
  let schema: object;

  switch (type) {
    case "Organization":
      schema = organizationSchema;
      break;
    case "WebSite":
      schema = websiteSchema;
      break;
    case "SoftwareApplication":
      schema = softwareApplicationSchema;
      break;
    case "MobileApplication":
      schema = mobileApplicationSchema;
      break;
    case "Article":
      if (!article) return null;
      schema = generateArticleSchema(article);
      break;
    case "BreadcrumbList":
      if (!breadcrumbs || breadcrumbs.length === 0) return null;
      schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: `https://godyspo.com${item.url}`
        }))
      };
      break;
    case "FAQPage":
      if (!faqs || faqs.length === 0) return null;
      schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer
          }
        }))
      };
      break;
    default:
      return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
